// plan-20 S02: the pass pipeline — a faithful port of the reference
// vague-ifier.html <script>. Pure canvas transforms plus the live loop, the
// mirror, the resolution governor, and the export orchestration. The reference
// is the ground truth for pipeline mechanics; this file changes only what the
// integration requires (host-box display lock instead of window; no global
// keyboard binding; callbacks instead of direct DOM).
//
// The golden pixel stamped at bitmap (48,48) here is ARTIFACT CONTENT (like the
// export cards' stamps), not the viewport singularity. PART IX #1 governs the
// fixed viewport pixel and is untouched (bible §34 note). The standing wave in
// wave48Pass runs 48 cycles down the FRAME of the processed image — artifact-
// internal, not the chrome standing wave (PART X note).

import { CIPHER, BAYER4, medianCutPalette, frameToIndices, buildGif } from './gif'
import { PRESET } from './preset'
import type { CaptionPos } from './preset'

const GOLDEN = '#DCB428'
const GOLDEN_RGB = [220, 180, 40]
const GREEN_INK = '#32A01E'
const MAX_SIDE = 2048
const LIVE_MAX_SIDE = 1080 // live modes process smaller for frame rate
const MIN_SIDE = 96
const FRAME_BUDGET_MS = 40 // governor: above this, live processing steps down
const FRAME_COMFORT_MS = 16 // below this, live processing steps back up
const BREATH_HZ = 0.8 // s3e11 BREATHING_RATE_HZ
const SIGIL_NUMS = [48, 93, 72, 13, 6, 8, 4, 11, 23, 33] // mathemagicks2 canon

// kingdom ramp: luma 0..1 -> color (RGB)
const RAMP = [
  { p: 0.00, c: [2, 6, 4] },
  { p: 0.15, c: [6, 10, 12] }, // screen-bg
  { p: 0.38, c: [0, 90, 0] }, // green-dim
  { p: 0.62, c: [0, 180, 0] }, // king-green
  { p: 0.82, c: [0, 210, 0] }, // term-green
  { p: 1.00, c: [180, 255, 180] },
]

// passes: envelope = smoothstep(a->b) over vagueness 0..48, scaled by multiplier.
export interface PassDef {
  id: string
  label: string
  a: number
  b: number
}
export const PASSES: PassDef[] = [
  { id: 'grade', label: 'kingdom grade', a: 0, b: 16 },
  { id: 'grain', label: 'grain', a: 2, b: 14 },
  { id: 'aberr', label: 'chromatic drift', a: 22, b: 40 },
  { id: 'quantize', label: 'green quantize', a: 16, b: 32 },
  { id: 'dither', label: 'bayer dither', a: 18, b: 34 },
  { id: 'posterize', label: 'posterize', a: 30, b: 44 },
  { id: 'scanlines', label: 'scanlines', a: 16, b: 30 },
  { id: 'wave48', label: 'standing wave 48', a: 6, b: 26 },
  { id: 'hsync', label: 'h-sync slips', a: 24, b: 40 },
  { id: 'mosaic', label: 'consensus mosaic', a: 34, b: 48 },
  { id: 'melt', label: 'edge melt / fog', a: 36, b: 48 },
  { id: 'matrix48', label: '48-matrix', a: 34, b: 48 },
  { id: 'bloom', label: 'phosphor bloom', a: 20, b: 36 },
  { id: 'mesh', label: 'cathedral mesh', a: 24, b: 40 },
  { id: 'vignette', label: 'vignette', a: 4, b: 18 },
  { id: 'glyphs', label: 'twilight glyphs', a: 36, b: 48 },
  { id: 'sigil', label: 'sigil circle', a: 40, b: 48 },
  { id: 'halo', label: 'pixel halo', a: 32, b: 48 },
]

const SCALE_NOTCHES = [1, 0.8, 0.65, 0.5, 0.4]
const EXPORT_D = 2.5 // loop seconds — two full breaths
const EXPORT_FPS = 12
const EXPORT_MAX_SIDE = 512

type Envelopes = Record<string, number>

interface AnimOverride {
  wave: (t: number) => number
  hex: (t: number) => number
  pent: (t: number) => number
  ring: (t: number) => number
  breathHz: number
}

export interface VagueState {
  v: number
  passes: Record<string, { on: boolean; mult: number }>
  caption: { on: boolean; pos: CaptionPos; size: number }
  inspector: boolean
}

export interface EngineHost {
  canvas: HTMLCanvasElement
  video: HTMLVideoElement
  stageBox: HTMLElement
  onStageLine: (text: string) => void
  emitOracle: (line: string) => void
  reducedMotion: () => boolean
  onSourceLoaded: () => void
  onWeaveBusy: (busy: boolean) => void
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6D2B79F5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function rampColor(t: number): number[] {
  t = Math.min(1, Math.max(0, t))
  for (let i = 1; i < RAMP.length; i++) {
    if (t <= RAMP[i].p) {
      const lo = RAMP[i - 1], hi = RAMP[i]
      const k = (t - lo.p) / (hi.p - lo.p)
      return [
        lo.c[0] + (hi.c[0] - lo.c[0]) * k,
        lo.c[1] + (hi.c[1] - lo.c[1]) * k,
        lo.c[2] + (hi.c[2] - lo.c[2]) * k,
      ]
    }
  }
  return RAMP[RAMP.length - 1].c
}

function crc32(bytes: Uint8Array): number {
  let c: number
  let crc = 0xFFFFFFFF
  for (let i = 0; i < bytes.length; i++) {
    c = (crc ^ bytes[i]) & 0xFF
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1
    crc = (crc >>> 8) ^ c
  }
  return (crc ^ 0xFFFFFFFF) >>> 0
}

// splice a tEXt chunk (keyword `probably`) carrying the cipher before IDAT
function withCipher(pngBytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const kw = 'probably'
  const data = new Uint8Array(kw.length + 1 + CIPHER.length)
  for (let i = 0; i < kw.length; i++) data[i] = kw.charCodeAt(i)
  for (let i = 0; i < CIPHER.length; i++) data[kw.length + 1 + i] = CIPHER.charCodeAt(i)
  const typeAndData = new Uint8Array(4 + data.length)
  typeAndData.set([0x74, 0x45, 0x58, 0x74]) // tEXt
  typeAndData.set(data, 4)
  const chunk = new Uint8Array(12 + data.length)
  new DataView(chunk.buffer).setUint32(0, data.length)
  chunk.set(typeAndData, 4)
  new DataView(chunk.buffer).setUint32(8 + data.length, crc32(typeAndData))
  let off = 8
  const dv = new DataView(pngBytes.buffer, pngBytes.byteOffset, pngBytes.byteLength)
  while (off < pngBytes.length) {
    const len = dv.getUint32(off)
    const type = String.fromCharCode(pngBytes[off + 4], pngBytes[off + 5], pngBytes[off + 6], pngBytes[off + 7])
    if (type === 'IDAT') break
    off += 12 + len
  }
  const out = new Uint8Array(pngBytes.length + chunk.length)
  out.set(pngBytes.subarray(0, off))
  out.set(chunk, off)
  out.set(pngBytes.subarray(off), off + chunk.length)
  return out
}

function loopAnimFor(D: number): AnimOverride {
  return {
    wave: (t) => 2 * Math.PI * t / D,
    hex: (t) => (Math.PI / 3) * t / D,
    pent: (t) => (2 * Math.PI / 5) * t / D,
    ring: () => 0,
    breathHz: Math.max(1, Math.round(BREATH_HZ * D)) / D,
  }
}

export interface WeaveResult {
  ok: boolean
  aborted: boolean
}

export class VagueEngine {
  readonly state: VagueState

  private host: EngineHost
  private preview: HTMLCanvasElement
  private pctx: CanvasRenderingContext2D
  private video: HTMLVideoElement

  private srcCanvas: HTMLCanvasElement | null = null
  private mode: 'static' | 'mirror' = 'static'
  private transmitting = false
  private mediaStream: MediaStream | null = null
  private frameIdx = 0
  private liveRAF = 0
  private liveT0 = 0
  private lastFrame = 0

  private baseW = 0
  private baseH = 0
  private procScale = 1
  private govNotch = 0
  private calmChecks = 0
  private govCooldown = 0
  private frameCost = 16
  private govCheck = 0
  private animOverride: AnimOverride | null = null
  private exporting = false

  private scratch: Record<string, HTMLCanvasElement> = {}
  private peeking = false
  private renderQueued = false
  private rippleStart = 0
  private destroyed = false
  private objectUrls: string[] = []

  constructor(host: EngineHost) {
    this.host = host
    this.preview = host.canvas
    this.video = host.video
    const ctx = this.preview.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new Error('vague-ifier: 2d context unavailable')
    this.pctx = ctx
    this.state = {
      v: 0,
      passes: {},
      caption: { ...PRESET.caption },
      inspector: PRESET.inspector,
    }
    for (const p of PASSES) {
      const preset = PRESET.passes[p.id]
      this.state.passes[p.id] = { on: preset ? preset.on : true, mult: preset ? preset.mult : 1 }
    }
  }

  private liveFps(): number {
    return this.host.reducedMotion() ? 12 : 24 // canon 24; halved for reduced motion
  }

  private liveActive(): boolean {
    return this.mode === 'mirror' || this.transmitting
  }

  private workSrc(): HTMLCanvasElement | null {
    return this.srcCanvas
  }

  hasSource(): boolean {
    return !!this.srcCanvas
  }

  isMirror(): boolean {
    return this.mode === 'mirror'
  }

  // ── governor + display lock ─────────────────────────────────────────────
  private setProcScale(s: number): void {
    this.procScale = s
    this.preview.width = Math.max(96, Math.round(this.baseW * s))
    this.preview.height = Math.max(96, Math.round(this.baseH * s))
  }

  private applyLiveScale(): void {
    const clamp = Math.min(1, LIVE_MAX_SIDE / Math.max(this.baseW, this.baseH))
    this.setProcScale(clamp * SCALE_NOTCHES[this.govNotch])
  }

  // freeze the canvas element's CSS box so processing-resolution changes never
  // move the layout (the resolution-jump bug). Measures the HOST box, not the
  // window, so the artifact fits the stage body (no page scroll, §42).
  lockDisplaySize(): void {
    if (!this.baseW) return
    const box = this.host.stageBox
    const availW = Math.max(96, box.clientWidth - 8)
    const availH = box.clientHeight > 40 ? box.clientHeight - 8 : window.innerHeight * 0.5
    const k = Math.min(1, availW / this.baseW, availH / this.baseH)
    this.preview.style.width = Math.round(this.baseW * k) + 'px'
    this.preview.style.height = Math.round(this.baseH * k) + 'px'
  }

  private getScratch(key: string, w: number, h: number): HTMLCanvasElement {
    let c = this.scratch[key]
    if (!c) { c = document.createElement('canvas'); this.scratch[key] = c }
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h }
    return c
  }

  private envelopes(): Envelopes {
    const E: Envelopes = {}
    for (const p of PASSES) {
      const t = this.state.passes[p.id]
      E[p.id] = t.on ? smoothstep(p.a, p.b, this.state.v) * t.mult : 0
    }
    return E
  }

  // ── image intake ─────────────────────────────────────────────────────────
  private intake(source: CanvasImageSource, w: number, h: number): void {
    let scale = 1
    if (Math.max(w, h) > MAX_SIDE) scale = MAX_SIDE / Math.max(w, h)
    if (Math.min(w, h) * scale < MIN_SIDE) scale = MIN_SIDE / Math.min(w, h)
    const W = Math.max(1, Math.round(w * scale))
    const H = Math.max(1, Math.round(h * scale))
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    const c = canvas.getContext('2d')
    if (!c) return
    c.imageSmoothingEnabled = scale < 1
    c.drawImage(source, 0, 0, W, H)
    this.srcCanvas = canvas
    this.baseW = W
    this.baseH = H
    this.setProcScale(1)
    this.host.onSourceLoaded()
    this.lockDisplaySize()
    this.scheduleRender()
  }

  loadFile(file: File | null | undefined): void {
    if (!file || !file.type.startsWith('image/')) { this.host.emitOracle('> the image was unreadable. probably.'); return }
    this.stopLive()
    this.mode = 'static'
    const img = new Image()
    const url = URL.createObjectURL(file)
    this.objectUrls.push(url)
    img.onload = () => {
      if (this.destroyed) return
      this.intake(img, img.naturalWidth, img.naturalHeight)
      URL.revokeObjectURL(url)
      this.objectUrls = this.objectUrls.filter((u) => u !== url)
    }
    img.onerror = () => {
      this.host.emitOracle('> the image was unreadable. probably.')
      URL.revokeObjectURL(url)
      this.objectUrls = this.objectUrls.filter((u) => u !== url)
    }
    img.src = url
  }

  testSpecimen(): void {
    const c = document.createElement('canvas')
    c.width = 640
    c.height = 800
    const x = c.getContext('2d')
    if (!x) return
    const bg = x.createLinearGradient(0, 0, 0, 800)
    bg.addColorStop(0, '#2a3140')
    bg.addColorStop(1, '#11141c')
    x.fillStyle = bg
    x.fillRect(0, 0, 640, 800)
    x.fillStyle = '#5a4632'
    x.beginPath()
    x.ellipse(320, 780, 260, 190, 0, Math.PI, 0)
    x.fill()
    x.fillStyle = '#c99b72'
    x.beginPath()
    x.ellipse(320, 380, 150, 190, 0, 0, Math.PI * 2)
    x.fill()
    x.fillStyle = '#31241c'
    x.beginPath()
    x.ellipse(320, 270, 155, 110, 0, Math.PI, 0)
    x.fill()
    x.fillStyle = '#1c1c22'
    x.beginPath(); x.ellipse(265, 380, 16, 10, 0, 0, Math.PI * 2); x.fill()
    x.beginPath(); x.ellipse(375, 380, 16, 10, 0, 0, Math.PI * 2); x.fill()
    x.strokeStyle = '#8a5a44'
    x.lineWidth = 5
    x.beginPath(); x.moveTo(285, 480); x.quadraticCurveTo(320, 495, 355, 480); x.stroke()
    const cols = ['#d43a3a', '#e0862c', '#e6d23c', '#3ab54a', '#3a6fd4', '#8a3ad4', '#ffffff']
    cols.forEach((col, i) => { x.fillStyle = col; x.fillRect(40 + i * 80, 700, 80, 60) })
    x.fillStyle = '#dfe4ea'
    x.font = '28px monospace'
    x.fillText('SPECIMEN 48', 220, 80)
    this.stopLive()
    this.mode = 'static'
    this.intake(c, 640, 800)
  }

  // ── the mirror (john dee's was obsidian) ───────────────────────────────
  async openMirror(): Promise<boolean> {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1024 } },
        audio: false,
      })
    } catch {
      this.host.emitOracle('> the mirror declined. probably.')
      return false
    }
    if (this.destroyed) {
      this.mediaStream.getTracks().forEach((t) => t.stop())
      this.mediaStream = null
      return false
    }
    this.video.srcObject = this.mediaStream
    try {
      await this.video.play()
    } catch {
      // some browsers reject play() without a visible element — the stream
      // still yields frames once readyState advances; carry on.
    }
    this.mode = 'mirror'
    const vw = this.video.videoWidth || 640
    const vh = this.video.videoHeight || 480
    let scale = 1
    if (Math.max(vw, vh) > LIVE_MAX_SIDE) scale = LIVE_MAX_SIDE / Math.max(vw, vh)
    const W = Math.round(vw * scale)
    const H = Math.round(vh * scale)
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    this.srcCanvas = canvas
    this.baseW = W
    this.baseH = H
    this.setProcScale(1)
    this.host.onSourceLoaded()
    this.lockDisplaySize()
    this.startLive()
    return true
  }

  private drawMirrorFrame(): void {
    if (this.video.readyState < 2 || !this.srcCanvas) return
    const c = this.srcCanvas.getContext('2d')
    if (!c) return
    const W = this.srcCanvas.width, H = this.srcCanvas.height
    c.save()
    c.translate(W, 0)
    c.scale(-1, 1) // a mirror mirrors
    c.drawImage(this.video, 0, 0, W, H)
    c.restore()
  }

  // ── transmit (a broadcast, not a picture) ──────────────────────────────
  toggleTransmit(): boolean {
    if (this.mode === 'mirror') return this.transmitting
    this.transmitting = !this.transmitting
    if (this.transmitting) {
      this.startLive()
    } else {
      this.stopLive()
      this.setProcScale(1)
      this.scheduleRender()
    }
    return this.transmitting
  }

  // ── live loop ─────────────────────────────────────────────────────────
  private startLive(): void {
    if (this.liveRAF) return
    this.liveT0 = performance.now()
    this.lastFrame = 0
    this.govCheck = 0
    this.applyLiveScale()
    this.liveRAF = requestAnimationFrame(this.liveTick)
  }

  private stopLive(): void {
    if (this.liveRAF) { cancelAnimationFrame(this.liveRAF); this.liveRAF = 0 }
    this.transmitting = false
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop())
      this.mediaStream = null
      this.video.srcObject = null
    }
    if (this.baseW) this.setProcScale(1)
  }

  private liveTick = (now: number): void => {
    if (this.destroyed || !this.liveActive()) { this.liveRAF = 0; return }
    const fps = this.liveFps()
    if (now - this.lastFrame >= 1000 / fps) {
      this.lastFrame = now
      this.frameIdx++
      if (this.mode === 'mirror') this.drawMirrorFrame()
      const c0 = performance.now()
      this.render((now - this.liveT0) / 1000)
      this.frameCost = this.frameCost * 0.85 + (performance.now() - c0) * 0.15
      // resolution governor: fixed notches, cooldown after any change, and a
      // 3-calm-checks rule before stepping back up — settles, no pumping.
      if (++this.govCheck >= fps) {
        this.govCheck = 0
        if (this.govCooldown > 0) {
          this.govCooldown--
        } else if (this.frameCost > FRAME_BUDGET_MS && this.govNotch < SCALE_NOTCHES.length - 1) {
          this.govNotch++
          this.calmChecks = 0
          this.govCooldown = 2
          this.applyLiveScale()
        } else if (this.frameCost < FRAME_COMFORT_MS && this.govNotch > 0) {
          if (++this.calmChecks >= 3) {
            this.calmChecks = 0
            this.govNotch--
            this.govCooldown = 2
            this.applyLiveScale()
          }
        } else {
          this.calmChecks = 0
        }
      }
    }
    this.liveRAF = requestAnimationFrame(this.liveTick)
  }

  // ── render pipeline ─────────────────────────────────────────────────────
  scheduleRender(): void {
    if (this.renderQueued || this.liveActive() || this.exporting || this.destroyed) return
    this.renderQueued = true
    requestAnimationFrame(() => { this.renderQueued = false; if (!this.destroyed) this.render(null) })
  }

  // t = seconds into the transmission, or null for a deterministic still
  private render(t: number | null): void {
    const src = this.workSrc()
    if (!src || this.peeking) return
    const W = this.preview.width, H = this.preview.height
    const E = this.envelopes()
    const p = this.pctx
    p.filter = 'none'
    p.globalAlpha = 1
    p.globalCompositeOperation = 'source-over'
    p.imageSmoothingEnabled = true
    p.drawImage(src, 0, 0, W, H)

    this.pixelPass(E, W, H, t)
    this.scanlinesPass(E, W, H)
    this.wave48Pass(E, W, H, t)
    this.hsyncPass(E, W, H, t)
    this.mosaicPass(E, W, H)
    this.meltPass(E, W, H)
    this.matrix48Pass(E, W, H)
    this.bloomPass(E, W, H)
    this.meshPass(E, W, H)
    this.vignettePass(E, W, H)
    this.glyphsPass(E, W, H, t)
    this.sigilPass(E, W, H, t)
    this.captionPass(W, H)
    this.pixelCeremony(E, W, H, t)
    this.inspectorPass(W, H)
    this.updateStageLine()

    if (!this.liveActive()) {
      if (this.rippleStart && performance.now() - this.rippleStart < 1600) {
        requestAnimationFrame(() => { if (!this.destroyed) this.render(null) })
      } else this.rippleStart = 0
    } else if (this.rippleStart && performance.now() - this.rippleStart >= 1600) {
      this.rippleStart = 0
    }
  }

  // fused per-pixel: grade + grain + chromatic drift + dither + quantize + posterize
  private pixelPass(E: Envelopes, W: number, H: number, t: number | null): void {
    const eG = E.grade, eGr = E.grain, eD = E.dither, eA = Math.min(1, E.aberr)
    const eQ = Math.min(1, E.quantize), eP = Math.min(1, E.posterize)
    if (eG <= 0 && eGr <= 0 && eD <= 0 && eQ <= 0 && eP <= 0 && eA <= 0) return
    const img = this.pctx.getImageData(0, 0, W, H)
    const d = img.data
    const src = eA > 0 ? new Uint8ClampedArray(d) : null
    const shift = Math.round(2 * eA)
    const rand = mulberry32(t == null ? 48 : 48 + (this.frameIdx & 0xFFFF)) // live grain boils
    const contrast = 1 + 0.18 * eG
    const desat = 0.12 * eG
    const levels = Math.max(3, Math.round(24 - 20 * eP))
    const lstep = 255 / (levels - 1)
    for (let y = 0; y < H; y++) {
      const brow = BAYER4[y & 3]
      for (let x = 0; x < W; x++) {
        const i = (y * W + x) * 4
        let r = d[i], g = d[i + 1], b = d[i + 2]
        if (src && shift > 0) {
          r = src[(y * W + Math.min(W - 1, x + shift)) * 4]
          b = src[(y * W + Math.max(0, x - shift)) * 4 + 2]
        }
        if (eG > 0) {
          r = (r - 128) * contrast + 128
          g = (g - 128) * contrast + 128
          b = (b - 128) * contrast + 128
          let lum = 0.2126 * r + 0.7152 * g + 0.0722 * b
          const sh = (1 - lum / 255) * eG * 0.25
          r += (8 - r) * sh
          g += (20 - g) * sh
          b += (14 - b) * sh
          lum = 0.2126 * r + 0.7152 * g + 0.0722 * b
          r += (lum - r) * desat
          g += (lum - g) * desat
          b += (lum - b) * desat
        }
        if (eGr > 0) {
          const n = (rand() - 0.5) * 28 * eGr
          r += n; g += n; b += n
        }
        if (eQ > 0) {
          let lum = 0.2126 * r + 0.7152 * g + 0.0722 * b
          if (eD > 0) lum += (brow[x & 3] / 16 - 0.5) * 64 * Math.min(1, eD)
          const kc = rampColor(lum / 255)
          r += (kc[0] - r) * eQ
          g += (kc[1] - g) * eQ
          b += (kc[2] - b) * eQ
        }
        if (eP > 0) {
          r += (Math.round(r / lstep) * lstep - r) * Math.min(1, eP * 1.5)
          g += (Math.round(g / lstep) * lstep - g) * Math.min(1, eP * 1.5)
          b += (Math.round(b / lstep) * lstep - b) * Math.min(1, eP * 1.5)
        }
        d[i] = r; d[i + 1] = g; d[i + 2] = b
      }
    }
    this.pctx.putImageData(img, 0, 0)
  }

  private scanlinesPass(E: Envelopes, W: number, H: number): void {
    const e = Math.min(1.5, E.scanlines)
    if (e <= 0) return
    this.pctx.fillStyle = `rgba(0, 0, 0, ${0.34 * e})`
    for (let y = 2; y < H; y += 3) this.pctx.fillRect(0, y, W, 1)
  }

  // the 48 Hz drone made visible — exactly 48 luminance cycles down the frame.
  private wave48Pass(E: Envelopes, W: number, H: number, t: number | null): void {
    const e = Math.min(1.5, E.wave48)
    if (e <= 0) return
    const op = 0.06 * e
    const phase = t == null ? 0 : (this.animOverride ? this.animOverride.wave(t) : t * 1.1)
    const strip = this.getScratch('wave', 1, H)
    const sc = strip.getContext('2d')
    if (!sc) return
    const img = sc.createImageData(1, H)
    for (let y = 0; y < H; y++) {
      img.data[y * 4 + 3] = Math.round(255 * op * (0.5 + 0.5 * Math.sin((y / H) * 48 * 2 * Math.PI + phase)))
    }
    sc.putImageData(img, 0, 0)
    this.pctx.drawImage(strip, 0, 0, 1, H, 0, 0, W, H)
  }

  private hsyncPass(E: Envelopes, W: number, H: number, t: number | null): void {
    const e = Math.min(1.5, E.hsync)
    if (e <= 0) return
    const seed = 4848 + (t == null ? 0 : Math.floor(t * 2.8) * 7)
    const pulse = t == null ? 1 : 0.55 + 0.45 * Math.sin(t * 1.3 + 0.7)
    const rand = mulberry32(seed)
    const bands = Math.floor(e * 7 * pulse)
    if (bands < 1) return
    const tmp = this.getScratch('hsync', W, H)
    const tc = tmp.getContext('2d')
    if (!tc) return
    tc.drawImage(this.preview, 0, 0)
    for (let i = 0; i < bands; i++) {
      const y0 = Math.floor(rand() * H)
      const bh = 2 + Math.floor(rand() * 5)
      const dx = Math.round((rand() - 0.5) * 2 * W * 0.06 * e)
      this.pctx.drawImage(tmp, 0, y0, W, bh, dx, y0, W, bh)
    }
  }

  private mosaicPass(E: Envelopes, W: number, H: number): void {
    const e = Math.min(1, E.mosaic)
    if (e <= 0) return
    const block = 1 + e * Math.max(3, Math.round(Math.min(W, H) / 56))
    if (block <= 1.5) return
    const dw = Math.max(1, Math.round(W / block))
    const dh = Math.max(1, Math.round(H / block))
    const tmp = this.getScratch('mosaic', dw, dh)
    const tc = tmp.getContext('2d')
    if (!tc) return
    tc.imageSmoothingEnabled = true
    tc.drawImage(this.preview, 0, 0, dw, dh)
    this.pctx.imageSmoothingEnabled = false
    this.pctx.globalAlpha = Math.min(1, e * 1.4)
    this.pctx.drawImage(tmp, 0, 0, dw, dh, 0, 0, W, H)
    this.pctx.globalAlpha = 1
    this.pctx.imageSmoothingEnabled = true
  }

  private meltPass(E: Envelopes, W: number, H: number): void {
    const e = Math.min(1, E.melt)
    if (e <= 0) return
    const tmp = this.getScratch('melt', W, H)
    const tc = tmp.getContext('2d')
    if (!tc) return
    tc.filter = `blur(${(e * 6).toFixed(1)}px)`
    tc.drawImage(this.preview, 0, 0)
    tc.filter = 'none'
    this.pctx.globalAlpha = 0.55 * e
    this.pctx.drawImage(tmp, 0, 0)
    this.pctx.globalAlpha = 1
    this.pctx.fillStyle = `rgba(10, 20, 12, ${0.3 * e})`
    this.pctx.fillRect(0, 0, W, H)
  }

  // THE 48-MATRIX: every block becomes a tiny "48" colored to the block average.
  private matrix48Pass(E: Envelopes, W: number, H: number): void {
    const e = Math.min(1, E.matrix48)
    if (e <= 0) return
    const block = Math.max(7, Math.round(Math.min(W, H) / 56))
    const img = this.pctx.getImageData(0, 0, W, H)
    const d = img.data
    const layer = this.getScratch('m48', W, H)
    const lc = layer.getContext('2d')
    if (!lc) return
    lc.fillStyle = '#000'
    lc.fillRect(0, 0, W, H)
    lc.font = `${Math.floor(block * 0.92)}px monospace`
    lc.textBaseline = 'alphabetic'
    const step = block >= 12 ? 2 : 1
    for (let by = 0; by + block <= H; by += block) {
      for (let bx = 0; bx + block <= W; bx += block) {
        let r = 0, g = 0, b = 0, n = 0
        for (let y = by; y < by + block; y += step) {
          for (let x = bx; x < bx + block; x += step) {
            const i = (y * W + x) * 4
            r += d[i]; g += d[i + 1]; b += d[i + 2]; n++
          }
        }
        r /= n; g /= n; b /= n
        const brightness = r * 0.3 + g * 0.59 + b * 0.11
        if (brightness < 5) continue
        const boost = Math.min(2.2, 255 / Math.max(1, Math.max(r, g, b))) * 0.7
        lc.fillStyle = `rgb(${Math.min(255, r * boost) | 0}, ${Math.min(255, g * boost) | 0}, ${Math.min(255, b * boost) | 0})`
        lc.fillText('48', bx, by + block - 2)
      }
    }
    this.pctx.globalAlpha = Math.min(1, e * 1.15)
    this.pctx.drawImage(layer, 0, 0)
    this.pctx.globalAlpha = 1
  }

  private bloomPass(E: Envelopes, W: number, H: number): void {
    const e = Math.min(1.5, E.bloom)
    if (e <= 0) return
    const dw = Math.max(1, W >> 2), dh = Math.max(1, H >> 2)
    const tmp = this.getScratch('bloom', dw, dh)
    const tc = tmp.getContext('2d')
    if (!tc) return
    tc.globalCompositeOperation = 'source-over'
    tc.filter = 'blur(3px)'
    tc.drawImage(this.preview, 0, 0, dw, dh)
    tc.filter = 'none'
    tc.globalCompositeOperation = 'source-atop'
    tc.fillStyle = 'rgba(0, 180, 80, 0.28)'
    tc.fillRect(0, 0, dw, dh)
    this.pctx.globalCompositeOperation = 'lighter'
    this.pctx.globalAlpha = 0.4 * e
    this.pctx.drawImage(tmp, 0, 0, dw, dh, 0, 0, W, H)
    this.pctx.globalAlpha = 1
    this.pctx.globalCompositeOperation = 'source-over'
  }

  private meshPass(E: Envelopes, W: number, H: number): void {
    const e = Math.min(1.5, E.mesh)
    if (e <= 0) return
    const spacing = Math.max(24, Math.round(Math.min(W, H) / 19))
    this.pctx.strokeStyle = `rgba(10, 18, 10, ${Math.min(0.6, 0.32 * e)})`
    this.pctx.lineWidth = 1
    this.pctx.beginPath()
    for (let x = spacing; x < W; x += spacing) { this.pctx.moveTo(x + 0.5, 0); this.pctx.lineTo(x + 0.5, H) }
    for (let y = spacing; y < H; y += spacing) { this.pctx.moveTo(0, y + 0.5); this.pctx.lineTo(W, y + 0.5) }
    this.pctx.stroke()
  }

  private vignettePass(E: Envelopes, W: number, H: number): void {
    const e = Math.min(1.5, E.vignette)
    if (e <= 0) return
    const r = Math.hypot(W, H) / 2
    const grad = this.pctx.createRadialGradient(W / 2, H / 2, r * 0.45, W / 2, H / 2, r)
    grad.addColorStop(0, 'rgba(0,0,0,0)')
    grad.addColorStop(1, `rgba(0,0,0,${0.42 * e})`)
    this.pctx.fillStyle = grad
    this.pctx.fillRect(0, 0, W, H)
  }

  private glyphsPass(E: Envelopes, W: number, H: number, t: number | null): void {
    const e = Math.min(1, E.glyphs)
    if (e <= 0) return
    const rng = mulberry32(77 + (t == null ? 0 : Math.floor(t * 1.4) * 13))
    const flick = t == null ? 1 : 0.7 + 0.3 * Math.sin(t * 2.1)
    const n = Math.floor((6 + 9 * e) * flick)
    const sc = Math.min(W, H) / 720
    for (let k = 0; k < n; k++) {
      const x0 = 10 + rng() * (W - 40)
      const y0 = 10 + rng() * (H - 40)
      const sz = (8 + rng() * 14) * sc
      const ang = rng() * Math.PI * 2
      const br = Math.floor(60 + 90 * rng())
      this.pctx.strokeStyle = `rgba(0, ${br}, 0, ${0.5 * e})`
      this.pctx.lineWidth = 1
      const nSeg = 3 + Math.floor(rng() * 3)
      let px = x0, py = y0
      this.pctx.beginPath()
      this.pctx.moveTo(px, py)
      for (let sgm = 0; sgm < nSeg; sgm++) {
        px += sz * Math.cos(ang + (rng() - 0.5) * 3)
        py += sz * Math.sin(ang + (rng() - 0.5) * 3)
        this.pctx.lineTo(px, py)
      }
      this.pctx.stroke()
      if (rng() < 0.3) {
        this.pctx.fillStyle = `rgba(0, ${br}, 0, ${0.5 * e})`
        this.pctx.beginPath()
        this.pctx.arc(x0, y0, 2 * sc, 0, Math.PI * 2)
        this.pctx.fill()
      }
    }
  }

  // the ritual circle — in a transmission it turns, exactly as canon draws it.
  private sigilPass(E: Envelopes, W: number, H: number, t: number | null): void {
    const e = Math.min(1, E.sigil)
    if (e <= 0) return
    const tt = t == null ? 0 : t
    const ANG = this.animOverride
      ? { hex: this.animOverride.hex(tt), pent: this.animOverride.pent(tt), ring: this.animOverride.ring(tt) }
      : { hex: tt * 0.15, pent: tt * 1.3 * 0.2, ring: tt * 0.2 }
    const cx = W / 2, cy = H / 2
    const r = 0.4 * Math.min(W, H)
    const p = this.pctx
    const st = (g: number, a: number): string => `rgba(0, ${g}, 0, ${a * e})`
    const circle = (rr: number, col: string): void => { p.strokeStyle = col; p.beginPath(); p.arc(cx, cy, rr, 0, Math.PI * 2); p.stroke() }
    p.lineWidth = 1
    circle(r, st(110, 0.5))
    circle(r * 0.78, st(80, 0.45))
    circle(r * 0.55, st(60, 0.4))
    for (const rot of [0, Math.PI]) {
      p.strokeStyle = st(140, 0.45)
      p.beginPath()
      for (let i = 0; i <= 3; i++) {
        const a = rot + i * (2 * Math.PI / 3) - Math.PI / 2 + ANG.hex
        const px = cx + r * 0.65 * Math.cos(a), py = cy + r * 0.65 * Math.sin(a)
        i === 0 ? p.moveTo(px, py) : p.lineTo(px, py)
      }
      p.stroke()
    }
    p.strokeStyle = st(100, 0.4)
    p.beginPath()
    const order = [0, 2, 4, 1, 3]
    order.concat([0]).forEach((i, k) => {
      const a = i * (2 * Math.PI / 5) - Math.PI / 2 + ANG.pent
      const px = cx + r * 0.42 * Math.cos(a), py = cy + r * 0.42 * Math.sin(a)
      k === 0 ? p.moveTo(px, py) : p.lineTo(px, py)
    })
    p.stroke()
    const fs = Math.max(9, Math.round(Math.min(W, H) * 0.022))
    p.font = `${fs}px monospace`
    SIGIL_NUMS.forEach((num, i) => {
      const a = i * (2 * Math.PI / SIGIL_NUMS.length) - Math.PI / 2 + ANG.ring
      const px = cx + r * 0.88 * Math.cos(a), py = cy + r * 0.88 * Math.sin(a)
      p.fillStyle = st(150, 0.55)
      p.beginPath(); p.arc(px, py, 2.5, 0, Math.PI * 2); p.fill()
      p.fillText(String(num), px - fs * 0.8, py + fs * 1.2)
    })
    p.strokeStyle = st(70, 0.35)
    for (let i = 0; i < SIGIL_NUMS.length / 2; i++) {
      const a1 = i * (2 * Math.PI / SIGIL_NUMS.length) - Math.PI / 2 + ANG.ring
      const a2 = (i + SIGIL_NUMS.length / 2) * (2 * Math.PI / SIGIL_NUMS.length) - Math.PI / 2 + ANG.ring
      p.beginPath()
      p.moveTo(cx + r * 0.55 * Math.cos(a1), cy + r * 0.55 * Math.sin(a1))
      p.lineTo(cx + r * 0.55 * Math.cos(a2), cy + r * 0.55 * Math.sin(a2))
      p.stroke()
    }
  }

  private captionPass(W: number, H: number): void {
    const cap = this.state.caption
    if (!cap.on) return
    const px = Math.round(Math.min(W, H) * cap.size / 100)
    this.pctx.font = `${px}px 'IBM Plex Mono', monospace`
    this.pctx.fillStyle = GREEN_INK
    const m = Math.round(Math.min(W, H) * 0.035)
    const tw = this.pctx.measureText('probably.').width
    let tx = m, ty = H - m
    this.pctx.textBaseline = 'alphabetic'
    if (cap.pos === 'br') tx = W - m - tw
    else if (cap.pos === 'tl') ty = m + px
    else if (cap.pos === 'tr') { tx = W - m - tw; ty = m + px }
    else if (cap.pos === 'cb') tx = (W - tw) / 2
    this.pctx.fillText('probably.', tx, ty)
  }

  // halo (breathing at 0.8 Hz) + THE pixel (artifact content, §34) + the ripple
  private pixelCeremony(E: Envelopes, W: number, H: number, t: number | null): void {
    const hz = this.animOverride ? this.animOverride.breathHz : BREATH_HZ
    const breath = t == null ? 1 : 0.8 + 0.2 * Math.sin(2 * Math.PI * hz * t)
    const eH = Math.min(1, E.halo) * breath
    if (eH > 0) {
      const grad = this.pctx.createRadialGradient(48.5, 48.5, 0, 48.5, 48.5, 14)
      grad.addColorStop(0, `rgba(${GOLDEN_RGB[0]}, ${GOLDEN_RGB[1]}, ${GOLDEN_RGB[2]}, ${0.35 * eH})`)
      grad.addColorStop(1, 'rgba(220, 180, 40, 0)')
      this.pctx.fillStyle = grad
      this.pctx.fillRect(34, 34, 30, 30)
    }
    this.pctx.fillStyle = GOLDEN
    this.pctx.fillRect(48, 48, 1, 1)
    if (this.rippleStart) {
      const rt = (performance.now() - this.rippleStart) / 1000
      const maxR = Math.min(280, Math.min(W, H) / 2)
      for (let i = 0; i < 6; i++) {
        const local = rt - i * 0.14
        if (local <= 0) continue
        const prog = Math.min(1, local / 1.2)
        const rr = prog * maxR
        if (rr <= 2) continue
        const a = Math.max(0, 1 - prog) * 0.7
        this.pctx.strokeStyle = `rgba(${GOLDEN_RGB[0]}, ${GOLDEN_RGB[1]}, ${GOLDEN_RGB[2]}, ${a})`
        this.pctx.lineWidth = 1
        this.pctx.beginPath()
        this.pctx.arc(48.5, 48.5, rr, 0, Math.PI * 2)
        this.pctx.stroke()
      }
    }
  }

  private inspectorPass(W: number, H: number): void {
    if (!this.state.inspector) return
    const p = this.pctx
    const s = Math.min(2, Math.max(0.8, Math.min(W, H) / 640))
    const pw = 176 * s, ph = 210 * s
    const x0 = W - pw - 12 * s, y0 = 12 * s
    p.fillStyle = 'rgba(8, 10, 6, 0.92)'
    p.fillRect(x0, y0, pw, ph)
    p.strokeStyle = 'rgba(160, 210, 0, 0.9)'
    p.lineWidth = 1
    p.strokeRect(x0 + 0.5, y0 + 0.5, pw, ph)
    p.font = `${Math.round(10 * s)}px monospace`
    p.textBaseline = 'alphabetic'
    p.fillStyle = 'rgba(160, 210, 0, 0.9)'
    p.fillText('PIXEL // ANCHOR', x0 + 10 * s, y0 + 14 * s)
    const cell = 18 * s
    const gx = x0 + 14 * s, gy = y0 + 24 * s
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const dx = gx + c * cell, dy = gy + r * cell
        p.strokeStyle = 'rgba(18, 22, 18, 1)'
        p.strokeRect(dx + 0.5, dy + 0.5, cell, cell)
        if (r === 4 && c === 4) {
          p.fillStyle = GOLDEN
          p.fillRect(dx + 1, dy + 1, cell - 2, cell - 2)
        }
      }
    }
    p.fillStyle = 'rgba(210, 220, 170, 0.9)'
    p.fillText('x=48  y=48', gx, gy + 8 * cell + 12 * s)
    p.fillStyle = 'rgba(150, 160, 110, 0.8)'
    p.fillText('zoom: 48x', gx, gy + 8 * cell + 26 * s)
  }

  private updateStageLine(): void {
    const v = this.state.v
    let line = 'pristine. for now.'
    if (v >= 48) line = 'the king remains.'
    else if (v >= 33) line = 'stage iii — the consensus'
    else if (v >= 17) line = 'stage ii — the broadcast'
    else if (v >= 1) line = 'stage i — the grade'
    if (this.liveActive()) {
      const fps = Math.min(this.liveFps(), Math.round(1000 / Math.max(1, this.frameCost)))
      const tag = this.mode === 'mirror' ? 'the mirror is live.' : 'transmitting.'
      line += `   // ${tag} ~${fps}fps @ ${this.procScale.toFixed(2)}x`
    }
    this.host.onStageLine(line)
  }

  // ── vagueness control ───────────────────────────────────────────────────
  setVagueness(v: number): void {
    if (v === 48 && this.state.v < 48) this.rippleStart = performance.now()
    this.state.v = v
    if (!this.liveActive() && Math.max(this.baseW, this.baseH) > 1200 && this.procScale === 1) this.setProcScale(0.55)
    this.scheduleRender()
  }

  commitVagueness(): void {
    if (!this.liveActive() && this.procScale !== 1) {
      this.setProcScale(1)
      this.scheduleRender()
    }
  }

  // ── tuning ──────────────────────────────────────────────────────────────
  setPass(id: string, patch: Partial<{ on: boolean; mult: number }>): void {
    const p = this.state.passes[id]
    if (!p) return
    if (patch.on !== undefined) p.on = patch.on
    if (patch.mult !== undefined) p.mult = Math.min(2, Math.max(0, patch.mult))
    this.rerender()
  }

  setCaption(patch: Partial<{ on: boolean; pos: CaptionPos; size: number }>): void {
    Object.assign(this.state.caption, patch)
    this.rerender()
  }

  setInspector(on: boolean): void {
    this.state.inspector = on
    this.rerender()
  }

  toggleInspectorAt(px: number, py: number): boolean {
    if (Math.hypot(px - 48, py - 48) < 30) {
      this.state.inspector = !this.state.inspector
      this.rerender()
      return true
    }
    return false
  }

  private rerender(): void {
    if (this.liveActive()) return
    this.scheduleRender()
  }

  toJson(): string {
    return JSON.stringify({ passes: this.state.passes, caption: this.state.caption, inspector: this.state.inspector })
  }

  // Applies pasted tuning JSON. Hardened against untrusted input: we iterate
  // OUR known pass ids (never the input's key set, so `__proto__`/`constructor`
  // can never reach a prototype), coerce every value to its expected type, and
  // whitelist the caption position. No value ever reaches innerHTML or eval —
  // they are all canvas draw params.
  applyJson(text: string): boolean {
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch {
      return false
    }
    if (!parsed || typeof parsed !== 'object') return false
    const obj = parsed as Record<string, unknown>

    const passes = obj.passes
    if (passes && typeof passes === 'object') {
      const src = passes as Record<string, unknown>
      for (const p of PASSES) {
        const raw = src[p.id]
        if (!raw || typeof raw !== 'object') continue
        const ps = raw as Record<string, unknown>
        this.state.passes[p.id].on = !!ps.on
        this.state.passes[p.id].mult = Math.min(2, Math.max(0, Number(ps.mult) || 0))
      }
    }

    const caption = obj.caption
    if (caption && typeof caption === 'object') {
      const c = caption as Record<string, unknown>
      if ('on' in c) this.state.caption.on = !!c.on
      const pos = c.pos
      if (pos === 'bl' || pos === 'br' || pos === 'tl' || pos === 'tr' || pos === 'cb') {
        this.state.caption.pos = pos
      }
      if ('size' in c) {
        const size = Number(c.size)
        if (Number.isFinite(size)) this.state.caption.size = Math.min(12, Math.max(2, size))
      }
    }

    if ('inspector' in obj) this.state.inspector = !!obj.inspector
    this.rerender()
    return true
  }

  // ── peek ────────────────────────────────────────────────────────────────
  startPeek(): void {
    if (!this.workSrc() || this.exporting) return
    this.peeking = true
    const src = this.workSrc()
    if (src) this.pctx.drawImage(src, 0, 0, this.preview.width, this.preview.height)
  }

  endPeek(): void {
    if (!this.peeking) return
    this.peeking = false
    this.scheduleRender()
  }

  // ── blessing (PNG download with tEXt cipher) ────────────────────────────
  bless(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.workSrc()) { resolve(false); return }
      this.preview.toBlob((blob) => {
        if (!blob) { resolve(false); return }
        blob.arrayBuffer().then((buf) => {
          const blessed = new Blob([withCipher(new Uint8Array(buf))], { type: 'image/png' })
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blessed)
          a.download = 'blessed.png'
          a.click()
          setTimeout(() => URL.revokeObjectURL(a.href), 1000)
          resolve(true)
        }).catch(() => resolve(false))
      }, 'image/png')
    })
  }

  // ── weave loop: export the transmission as a seamless looping gif ────────
  async exportLoop(): Promise<WeaveResult> {
    if (!this.srcCanvas || this.exporting) return { ok: false, aborted: false }
    this.exporting = true
    this.host.onWeaveBusy(true)
    // rAF races a timeout: rAF stalls in background tabs (and headless), and an
    // export must finish even if the user tabs away mid-weave.
    const nextFrame = (): Promise<void> => new Promise((resolve) => {
      let done = false
      const fin = (): void => { if (!done) { done = true; resolve() } }
      requestAnimationFrame(fin)
      setTimeout(fin, 60)
    })
    const D = EXPORT_D
    const fps = EXPORT_FPS
    const nFrames = Math.round(D * fps)
    const maxSide = EXPORT_MAX_SIDE
    const wasLive = this.liveActive()
    if (this.liveRAF) { cancelAnimationFrame(this.liveRAF); this.liveRAF = 0 }
    const savedRipple = this.rippleStart
    const savedScale = this.procScale
    const savedFrameIdx = this.frameIdx
    this.rippleStart = 0
    this.setProcScale(Math.min(1, maxSide / Math.max(this.baseW, this.baseH)))
    const W = this.preview.width, H = this.preview.height
    this.animOverride = loopAnimFor(D)
    const frames: ImageData[] = []
    for (let f = 0; f < nFrames; f++) {
      if (this.destroyed) { this.exporting = false; return { ok: false, aborted: true } }
      this.frameIdx = f
      this.render(f / fps)
      frames.push(this.pctx.getImageData(0, 0, W, H))
      this.host.onStageLine(`weaving the loop... frame ${f + 1}/${nFrames}`)
      await nextFrame()
    }
    this.animOverride = null
    this.frameIdx = savedFrameIdx
    this.host.onStageLine('weaving the loop... reading the pattern')
    await nextFrame()
    if (this.destroyed) { this.exporting = false; return { ok: false, aborted: true } }
    const stride = Math.max(1, Math.round((W * H * nFrames) / 60000))
    const sampleList: number[] = []
    for (const fr of frames) {
      const total = fr.width * fr.height
      for (let i = 0; i < total; i += stride) {
        sampleList.push(fr.data[i * 4], fr.data[i * 4 + 1], fr.data[i * 4 + 2])
      }
    }
    const { pal, count } = medianCutPalette(new Uint8Array(sampleList), 256)
    const cache = new Int16Array(32768).fill(-1)
    const indexFrames: Uint8Array[] = []
    for (let f = 0; f < frames.length; f++) {
      if (this.destroyed) { this.exporting = false; return { ok: false, aborted: true } }
      indexFrames.push(frameToIndices(frames[f], pal, count, cache))
      if (f % 5 === 4) {
        this.host.onStageLine(`weaving the loop... quantizing ${f + 1}/${nFrames}`)
        await nextFrame()
      }
    }
    this.host.onStageLine('weaving the loop... binding')
    await nextFrame()
    const delayCs = Math.max(2, Math.round(100 / fps))
    const bytes = buildGif(W, H, indexFrames, pal, delayCs)
    const blob = new Blob([bytes], { type: 'image/gif' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'transmission.gif'
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 1000)
    this.rippleStart = savedRipple
    this.setProcScale(savedScale)
    this.exporting = false
    this.host.onWeaveBusy(false)
    if (wasLive && !this.destroyed) { this.lastFrame = 0; this.liveRAF = requestAnimationFrame(this.liveTick) }
    else if (!this.destroyed) this.scheduleRender()
    return { ok: true, aborted: false }
  }

  // ── teardown ────────────────────────────────────────────────────────────
  reset(): void {
    this.stopLive()
    this.mode = 'static'
    this.srcCanvas = null
    this.transmitting = false
    this.setProcScaleSafe()
  }

  private setProcScaleSafe(): void {
    this.procScale = 1
    this.govNotch = 0
  }

  destroy(): void {
    this.destroyed = true
    if (this.liveRAF) { cancelAnimationFrame(this.liveRAF); this.liveRAF = 0 }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop())
      this.mediaStream = null
    }
    this.video.srcObject = null
    for (const u of this.objectUrls) URL.revokeObjectURL(u)
    this.objectUrls = []
    this.srcCanvas = null
    this.scratch = {}
  }
}
